import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { MachineLocation } from '@/shared/hooks/api/machineryDirectiveCompliance'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { dialogPortaledPopoverGuard } from '@/components/ui/dialog-portaled-popover-guard'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { useMachineryDirectiveEhsrList } from '../../hooks/useMachineryDirectiveEhsrList'
import { EhsrSelect } from './EhsrSelect'
import { HAZARD_LIFECYCLES, HAZARD_TYPES } from './constants/hazardEnums'
import {
  getMachineLocationDisplayName,
  sortMachineLocationsForList,
} from '../machine-locations/buildMachineLocationRows'
import {
  NO_CATEGORY,
  NO_LIFECYCLE,
  NO_LOCATION,
  defaultHazardListFilters,
  type HazardListFilters,
} from './hazardListFilters'

type HazardFiltersSheetProps = {
  filters: HazardListFilters
  onFiltersChange: (filters: HazardListFilters) => void
  open: boolean
  onOpenChange: (open: boolean) => void
  machineLocations: MachineLocation[]
}

export function HazardFiltersSheet({
  filters,
  onFiltersChange,
  open,
  onOpenChange,
  machineLocations,
}: HazardFiltersSheetProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const ehsrOptions = useMachineryDirectiveEhsrList()
  const [draftFilters, setDraftFilters] = useState<HazardListFilters>(filters)

  useEffect(() => {
    if (open) {
      setDraftFilters(filters)
    }
  }, [open, filters])

  const sortedMachineLocations = useMemo(
    () => sortMachineLocationsForList(machineLocations),
    [machineLocations]
  )

  const handleApply = useCallback(() => {
    onFiltersChange(draftFilters)
    onOpenChange(false)
  }, [draftFilters, onFiltersChange, onOpenChange])

  const handleClearAll = useCallback(() => {
    setDraftFilters(defaultHazardListFilters)
  }, [])

  const toggleLifecycle = useCallback((lifecycle: string) => {
    setDraftFilters((previous) => {
      const isSelected = previous.lifecycles.includes(lifecycle)
      if (isSelected) {
        return {
          ...previous,
          lifecycles: previous.lifecycles.filter(
            (value) => value !== lifecycle
          ),
        }
      }
      return {
        ...previous,
        lifecycles: [...previous.lifecycles, lifecycle],
      }
    })
  }, [])

  const adequacyFilterItems = useMemo(
    () => [
      { value: 'all', label: t('hazardsPage.filterAdequacyAll') },
      {
        value: 'not_adequately_reduced',
        label: t('hazardsPage.filterAdequacyNotReduced'),
      },
      {
        value: 'adequately_reduced',
        label: t('hazardsPage.filterAdequacyReduced'),
      },
    ],
    [t]
  )
  const categoryFilterItems = useMemo(() => {
    const items = [
      { value: 'all', label: t('hazardsPage.filterAdequacyAll') },
      { value: NO_CATEGORY, label: t('hazardsPage.filterNoCategory') },
    ]
    for (const value of HAZARD_TYPES) {
      items.push({
        value,
        label: t(`hazardsPage.enums.hazardTypes.${value}`),
      })
    }
    return items
  }, [t])
  const locationFilterItems = useMemo(() => {
    const items = [
      { value: 'all', label: t('hazardsPage.filterAdequacyAll') },
      { value: NO_LOCATION, label: t('hazardsPage.filterNoLocation') },
    ]
    for (const location of sortedMachineLocations) {
      items.push({
        value: location.id,
        label: getMachineLocationDisplayName(location, t),
      })
    }
    return items
  }, [sortedMachineLocations, t])
  const sourceFilterItems = useMemo(
    () => [
      { value: 'all', label: t('hazardsPage.filterSourceAll') },
      { value: 'USER', label: t('hazardsPage.filterSourceUser') },
      { value: 'AGENT', label: t('hazardsPage.filterSourceAgent') },
    ],
    [t]
  )

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side='right'
        className='flex w-full flex-col sm:max-w-md'
        {...dialogPortaledPopoverGuard}
      >
        <SheetHeader>
          <SheetTitle>{t('hazardsPage.filtersTitle')}</SheetTitle>
        </SheetHeader>

        <ScrollArea className='min-h-0 flex-1 px-4'>
          <div className='space-y-6 pb-4'>
            <div className='space-y-2'>
              <Label>{t('hazardsPage.filterAdequacyLabel')}</Label>
              <Select
                value={draftFilters.adequacy}
                onValueChange={(value) => {
                  if (!value) {
                    return
                  }
                  setDraftFilters((previous) => ({
                    ...previous,
                    adequacy: value as HazardListFilters['adequacy'],
                  }))
                }}
                items={adequacyFilterItems}
              >
                <SelectTrigger className='w-full'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>
                    {t('hazardsPage.filterAdequacyAll')}
                  </SelectItem>
                  <SelectItem value='not_adequately_reduced'>
                    {t('hazardsPage.filterAdequacyNotReduced')}
                  </SelectItem>
                  <SelectItem value='adequately_reduced'>
                    {t('hazardsPage.filterAdequacyReduced')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className='space-y-2'>
              <Label>{t('hazardsPage.typeLabel')}</Label>
              <Select
                value={draftFilters.category}
                onValueChange={(value) =>
                  setDraftFilters((previous) => ({
                    ...previous,
                    category: value ?? '',
                  }))
                }
                items={categoryFilterItems}
              >
                <SelectTrigger className='w-full'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>
                    {t('hazardsPage.filterAdequacyAll')}
                  </SelectItem>
                  <SelectItem value={NO_CATEGORY}>
                    {t('hazardsPage.filterNoCategory')}
                  </SelectItem>
                  {HAZARD_TYPES.map((value) => (
                    <SelectItem key={value} value={value}>
                      {t(`hazardsPage.enums.hazardTypes.${value}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className='space-y-2'>
              <Label>{t('hazardsPage.locationLabel')}</Label>
              <Select
                value={draftFilters.location}
                onValueChange={(value) =>
                  setDraftFilters((previous) => ({
                    ...previous,
                    location: value ?? '',
                  }))
                }
                items={locationFilterItems}
              >
                <SelectTrigger className='w-full'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>
                    {t('hazardsPage.filterAdequacyAll')}
                  </SelectItem>
                  <SelectItem value={NO_LOCATION}>
                    {t('hazardsPage.filterNoLocation')}
                  </SelectItem>
                  {sortedMachineLocations.map((location) => (
                    <SelectItem key={location.id} value={location.id}>
                      {getMachineLocationDisplayName(location, t)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className='space-y-2'>
              <Label>{t('hazardsPage.hazardDetail.ehsr')}</Label>
              <EhsrSelect
                options={ehsrOptions}
                value={draftFilters.ehsrClauseIds}
                onChange={(ehsrClauseIds) =>
                  setDraftFilters((previous) => ({
                    ...previous,
                    ehsrClauseIds,
                  }))
                }
              />
              <div className='flex items-center gap-2'>
                <Checkbox
                  id='hazard-filter-no-ehsr'
                  checked={draftFilters.includeNoEhsr}
                  onCheckedChange={(checked) =>
                    setDraftFilters((previous) => ({
                      ...previous,
                      includeNoEhsr: checked === true,
                    }))
                  }
                />
                <Label
                  htmlFor='hazard-filter-no-ehsr'
                  className='cursor-pointer text-sm font-normal'
                >
                  {t('hazardsPage.filterNoEhsr')}
                </Label>
              </div>
            </div>

            <div className='space-y-2'>
              <Label>{t('hazardsPage.lifecyclesLabel')}</Label>
              <div className='flex flex-col gap-3 rounded-md border bg-muted/30 p-3'>
                {HAZARD_LIFECYCLES.map((value) => (
                  <div key={value} className='flex items-center gap-2'>
                    <Checkbox
                      id={`hazard-filter-lifecycle-${value}`}
                      checked={draftFilters.lifecycles.includes(value)}
                      onCheckedChange={() => toggleLifecycle(value)}
                    />
                    <Label
                      htmlFor={`hazard-filter-lifecycle-${value}`}
                      className='cursor-pointer text-sm font-normal'
                    >
                      {t(`hazardsPage.enums.hazardLifecycles.${value}`)}
                    </Label>
                  </div>
                ))}
                <div className='flex items-center gap-2'>
                  <Checkbox
                    id='hazard-filter-no-lifecycle'
                    checked={draftFilters.lifecycles.includes(NO_LIFECYCLE)}
                    onCheckedChange={() => toggleLifecycle(NO_LIFECYCLE)}
                  />
                  <Label
                    htmlFor='hazard-filter-no-lifecycle'
                    className='cursor-pointer text-sm font-normal'
                  >
                    {t('hazardsPage.filterNoLifecycle')}
                  </Label>
                </div>
              </div>
            </div>

            <div className='space-y-2'>
              <Label>{t('hazardsPage.filterSourceLabel')}</Label>
              <Select
                value={draftFilters.source}
                onValueChange={(value) => {
                  if (!value) {
                    return
                  }
                  setDraftFilters((previous) => ({
                    ...previous,
                    source: value as HazardListFilters['source'],
                  }))
                }}
                items={sourceFilterItems}
              >
                <SelectTrigger className='w-full'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>
                    {t('hazardsPage.filterSourceAll')}
                  </SelectItem>
                  <SelectItem value='USER'>
                    {t('hazardsPage.filterSourceUser')}
                  </SelectItem>
                  <SelectItem value='AGENT'>
                    {t('hazardsPage.filterSourceAgent')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </ScrollArea>

        <SheetFooter className='flex-row gap-2 border-t pt-4'>
          <Button
            type='button'
            variant='outline'
            className='flex-1'
            onClick={handleClearAll}
          >
            {t('hazardsPage.filtersClearAll')}
          </Button>
          <Button type='button' className='flex-1' onClick={handleApply}>
            {t('hazardsPage.filtersApply')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
